<template>
    <div>
        <button-close-filter
            ref="closeFilter"
        />

        <card>
            <card-header color="primary">Filter</card-header>
            <card-body>
                <div class="form-group">
                    <label>Region</label>
                    <multiselect :options="regions" v-model="region" track-by="id" label="name"
                                 :loading="loadingRegions"></multiselect>
                </div>
                <div class="form-group">
                    <label>Location</label>
                    <multiselect :options="locations" v-model="location" track-by="id" label="name" @select="selectLocation"
                                 @remove="deselectLocation" :loading="loadingLocations"></multiselect>
                </div>
                <div class="form-group" v-if="showCampaigns">
                    <label>Campaign</label>
                    <multiselect :options="campaigns" v-model="campaign" track-by="id" label="name"
                                 :loading="loadingCampaigns"></multiselect>
                </div>
                <div class="form-group" v-if="showTags">
                    <label>Tag</label>
                    <multiselect :max=5 :options-limit=5 :options="tags" v-model="tag" track-by="id" label="name"
                                 :loading="loadingTags"></multiselect>
                </div>
                <div class="form-group">
                    <label>Date Range</label>
                    <multiselect :options="dateRanges" v-model="fixedDateRange"
                                 @input="setFixedDateRange(fixedDateRange.id)" track-by="id" label="name"
                                 :loading="loadingTags"></multiselect>
                </div>
                <v-date-picker class="d-block" mode="range" v-model="dateRange" :max-date="new Date()"
                               :input-props="{class: 'form-control'}" show-caps="show-caps" popover-visibility="focus">
                    <div class="input-group mb-3" slot-scope="{ inputValue, updateValue }">
                        <input class="form-control"
                               :value="inputValue"
                               @change.native="updateValue($event.target.value)"
                               @click="fixedDateRange = null"/>
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary" @click="dateRange = null" type="button">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                </v-date-picker>
            </card-body>
            <card-footer>
                <div class="form-group">
                    <input-search v-model="search"></input-search>
                </div>
                <div class="form-row">
                    <div class="col-4 col-lg-6">
                        <button class="btn btn-default btn-block py-2" @click="reset">Reset</button>
                    </div>
                    <div class="col-4 col-lg-6 ml-auto">
                        <button class="btn btn-outline-primary btn-block py-2" @click="apply"
                                :class="{'disabled': filterIsEmpty}" :disabled="filterIsEmpty">
                            Apply
                        </button>
                    </div>
                </div>
            </card-footer>
        </card>
    </div>
</template>

<script>
    import {mapMutations, mapState} from 'vuex';
    import mutations                from '../../store/mutation-types';

    export default {
        props: {
            showTags: {
                required: false,
                type: Boolean,
                default: () => true
            },

            showCampaigns: {
                required: false,
                type: Boolean,
                default: () => true
            }
        },

        data() {
            return {
                campaigns       : [],
                locations       : [],
                regions         : [],
                tags            : [],
                dateRanges      : [],
                loadingLocations: true,
                loadingRegions  : true,
                loadingCampaigns: true,
                loadingTags     : true,
                location        : null,
                region          : null,
                tag             : null,
                campaign        : null,
                dateRange       : null,
                search          : null,
                fixedDateRange  : null,
            }
        },

        created() {
            this.fetchCampaigns();
            this.fetchLocations();
            this.fetchRegions();
            this.fetchTags();
            this.fetchDateRanges();
            window.Bus.$on('updateInputTags', () => this.fetchTags());
        },

        computed: {
            filterIsEmpty() {
                return this.location === null &&
                       this.campaign === null &&
                       this.region === null &&
                       this.tag === null &&
                       this.dateRange === null &&
                       this.search === null &&
                       this.fixedDateRange === null;
            },
            ...mapState({
                filter: state => state.filter,
            })
        },

        watch: {
            filter(filter) {
                this.dateRange      = filter.dateRange;
                this.search         = filter.search;
                this.location       = this.locations.find((location) => location.id === filter.location_id);
                this.campaign       = this.campaigns.find((campaign) => campaign.id === filter.campaign_id);
                this.region         = this.regions.find((region) => region.id === filter.region_id);
                this.tag            = this.tags.find((tag) => tag.id === filter.tag_id);
                this.fixedDateRange = filter.fixedDateRange;
            },
        },

        methods: {
            fetchDateRanges() {
                this.dateRanges = [
                    {id: 365, name: "Last 365 days"},
                    {id: 30, name: "Last 30 days"},
                    {id: 7, name: "Last 7 days"},
                ];
            },
            fetchCampaigns() {
                this.$http.get(this.$laroute.route('api.global-filters.campaign'))
                    .then(({data}) => {
                        this.campaigns        = data.data;
                        this.loadingCampaigns = false;
                    });
            },

            fetchLocations() {
                this.$http.get(this.$laroute.route('api.global-filters.location'))
                    .then(({data}) => {
                        this.locations        = data.data;
                        this.loadingLocations = false;
                    });
            },

            fetchRegions() {
                this.$http.get(this.$laroute.route('api.tags.index', {type: 'region'}))
                    .then(({data}) => {
                        this.regions        = data.data;
                        this.loadingRegions = false;
                    });
            },

            fetchTags() {
                this.$http.get(this.$laroute.route('api.tags.index'))
                    .then(({data}) => {
                        this.tags        = data.data;
                        this.loadingTags = false;
                    });
            },

            ...mapMutations({
                resetFilter: mutations.RESET_FILTER,
                update     : mutations.UPDATE_FILTER,
            }),

            reset() {
                this.getPathFromUrl();
                this.deselectLocation();
                this.resetFilter();
                this.$refs.closeFilter.close();
            },

            setFixedDateRange(days) {
                let date = new Date();
                date.setDate(date.getDate() - days);
                this.dateRange = {start: date, end: new Date()};
            },

            getPathFromUrl() {
                const actualURL = window.location.href.split("?");
                return (actualURL.length > 1)
                       ? window.location.href = actualURL[0]
                       : null
            },

            selectLocation(location) {
                this.campaigns = location.campaigns;
            },

            deselectLocation() {
                this.loadingCampaigns = true;
                this.fetchCampaigns();
            },

            apply() {
                const filter = {
                    dateRange     : this.dateRange,
                    search        : this.search,
                    location_id   : null,
                    region_id     : null,
                    tag_id        : null,
                    campaign_id   : null,
                    fixedDateRange: this.fixedDateRange,
                };

                if (this.location) {
                    filter.location_id = this.location.id;
                }

                if (this.region) {
                    filter.region_id = this.region.id;
                }

                if (this.tag) {
                    filter.tag_id = this.tag.id;
                }

                if (this.campaign) {
                    filter.campaign_id = this.campaign.id;
                }

                this.update(filter);

                this.$refs.closeFilter.close();
            },
        }
    }
</script>
